'use client';
import { useState } from 'react';

import { Article, Delete, Download, OpenInBrowser } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';
import { GetWorkOrderFilesMediaPayloadModel } from '@lichtblick/types';

import {
  GridItemContainer,
  HoverContainer,
  MobileImageWrapper,
  MobileControlsContainer,
  MobileFileName,
  MobileButtonsGroup,
  SingleCheckboxWrapper,
  GridMobileOnly,
  GridDesktopOnly,
} from './DocPreview.styles';
import { mimeTypeMapper } from './utils';

import { Checkbox } from '../Checkbox';
import { Text } from '../Text';

type FileGridItemProps = {
  allowMultiSelect?: boolean;
  isSelected?: boolean;
  item: GetWorkOrderFilesMediaPayloadModel;
  onClick: (file: GetWorkOrderFilesMediaPayloadModel) => void;
  onDelete: (file: GetWorkOrderFilesMediaPayloadModel) => void;
  onDownload: (file: GetWorkOrderFilesMediaPayloadModel) => void;
  onOpenInTab: (file: GetWorkOrderFilesMediaPayloadModel) => Promise<void>;
  onSelectionChange?: (fileId: number) => void;
  renderByMimeType: (file: GetWorkOrderFilesMediaPayloadModel) => React.ReactElement;
};

export const FileGridItem = ({
  allowMultiSelect = false,
  isSelected = false,
  item,
  onClick,
  onDelete,
  onDownload,
  onOpenInTab,
  onSelectionChange,
  renderByMimeType,
}: FileGridItemProps) => {
  const [shouldShowControls, setShouldShowControls] = useState<boolean>(false);

  if (!item.mimeType) {
    return <Article />;
  }

  return (
    <GridItemContainer
      onClick={(e) => {
        e.stopPropagation();
        onClick(item);
      }}
      onMouseEnter={() => setShouldShowControls(true)}
      onMouseLeave={() => setShouldShowControls(false)}
    >
      <GridMobileOnly>
        <MobileImageWrapper>{renderByMimeType(item)}</MobileImageWrapper>
        <MobileControlsContainer>
          <MobileFileName>{item.fileName || item.folderName || mimeTypeMapper[item.mimeType]}</MobileFileName>
          <MobileButtonsGroup>
            {allowMultiSelect && (
              <SingleCheckboxWrapper
                data-checkbox="true"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectionChange?.(item.id);
                }}
              >
                <Checkbox isChecked={isSelected} />
              </SingleCheckboxWrapper>
            )}
            <MobileButtonsGroup $alignRight={true}>
              <OpenInBrowser
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenInTab(item);
                }}
              />
              <Download
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(item);
                }}
              />
              <Delete
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item);
                }}
              />
            </MobileButtonsGroup>
          </MobileButtonsGroup>
        </MobileControlsContainer>
      </GridMobileOnly>
      <GridDesktopOnly>
        <HoverContainer $isShowing={shouldShowControls || isSelected}>
          {allowMultiSelect && (
            <SingleCheckboxWrapper
              data-checkbox="true"
              onClick={(e) => {
                e.stopPropagation();
                onSelectionChange?.(item.id);
              }}
            >
              <Checkbox isChecked={isSelected} />
            </SingleCheckboxWrapper>
          )}

          <div>
            <Text $variant={TextVariants.body.s} color={Colors.white}>
              {mimeTypeMapper[item.mimeType]}
            </Text>
            <OpenInBrowser
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                onOpenInTab(item);
              }}
            />
            <Download
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(item);
              }}
            />
            <Delete
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item);
              }}
            />
          </div>
        </HoverContainer>
        {renderByMimeType(item)}
      </GridDesktopOnly>
    </GridItemContainer>
  );
};
